import z from 'zod';
import { statusPackWarningsSchema } from './status-pack-warning.schema.js';
/** The shared AD/SB status enum (`ADSBStatus`) as zod values, for the DR-5764 `vendorSbs` items. */
const ADSB_STATUS_VALUES = [
    'closed',
    'open',
    'not-applicable',
    'unknown',
    'superseded',
    'canceled',
];
export const qecListEventsReadyWebhookSchema = z.looseObject({
    type: z.literal('qec-list-events-ready'),
    projectId: z.string(),
    // `z.iso.datetime()`, not a bare string: the consumer contract is an ORDERABLE instant,
    // so the schema enforces what the field documents. Follows `pma-der-events-ready` /
    // `document-header-ready` rather than the looser `project-progress` / `project-data-ready`
    // spelling.
    //
    // `.optional()` is a ROLLOUT concession, not a statement that the stamp is dispensable —
    // Brain always emits it (see the family's builder, pinned by its test). This schema is a
    // member of `webhookPayloadSchema`, so a REQUIRED field would make a consumer that
    // upgrades `@avisoma/shared` reject every older, unstamped envelope still in flight — and reject
    // ALL of them for as long as the consumer runs ahead of Brain's own deploy. Tighten to
    // required in a later release, once no unstamped envelope can still be in flight (DR-3278).
    generatedAt: z.iso.datetime().optional(),
    generation: z.number().int().positive().max(2_147_483_647).optional(),
    documentId: z.string(),
    // The winning entity's stable id (reconstructed document) — a QEC list can
    // span multiple source documents, so the entity id is the durable reference.
    reconstructedDocumentId: z.string(),
    pageIds: z.array(z.string()),
    // Entity-level resolved ESN of the winning list, when one was extracted.
    esn: z.string().optional(),
    qecListEvents: z.array(z.looseObject({
        rowIndex: z.number(),
        description: z.string(),
        oemPartNumber: z.string().optional(),
        vendorPartNumber: z.string().optional(),
        serialNumber: z.string().optional(),
        // DR-2024 install-status enum; `statusRaw` carries the verbatim document string.
        status: z
            .enum(['Installed', 'Not Applicable', 'Not Supplied', 'Not Installed', 'Not Visible', 'Not Required'])
            .optional(),
        statusRaw: z.string().optional(),
        quantity: z.number().optional(),
        position: z.string().optional(),
        remarks: z.string().optional(),
        date: z.string().optional(),
        category: z.enum(['QEC', 'LRU']).optional(),
        ipcReference: z.string().optional(),
        ataReference: z.string().optional(),
        tsn: z.number().optional(),
        csn: z.number().optional(),
        tso: z.number().optional(),
        cso: z.number().optional(),
        tsr: z.number().optional(),
        csr: z.number().optional(),
        tsi: z.number().optional(),
        csi: z.number().optional(),
        // DR-5996: hours / cycles since this part was installed (the date is `dateInstalled`), filled
        // only for rows read from a combined TSN / TSI / TSO column. Distinct from `tsi` / `csi`, which
        // stay time since INSPECTION.
        tsInstall: z.number().optional(),
        csInstall: z.number().optional(),
        // DR-5996: release certificate date → install date with no serviceable tag or inspected release
        // certificate in the pack covering it (Rinoj 2026-10-29). Absent when there is no gap.
        recordsGap: z.looseObject({ fromDate: z.string(), toDate: z.string() }).optional(),
        dateInstalled: z.string().optional(),
        // DR-5094: the installation cell read "ORIG" (original fit at aircraft build). Only `true`;
        // absent otherwise, and a row with it carries no `dateInstalled`. Its birth evidence is the
        // aircraft delivery record (`build-record-events-ready`), not a release certificate.
        originalFit: z.literal(true).optional(),
        // DR-5764: the SB-shaped control items a TRAX P/N Detail Print prints under this part's block,
        // attached by P/N + exact serial. `kind` is Brain's call (DR-6278): `engine` only when the number
        // carries the engine OEM token, CFM for now (`SB CFM 73-0193`), `vendor` for every other SB
        // printed on the part; a service letter, operator item or WestJet task code is never an item;
        // `pageId` is the print page the item was read from. Absent when the row has none; never null;
        // never an empty array.
        vendorSbs: z
            .array(z.object({
            sbNumber: z.string(),
            kind: z.enum(['engine', 'vendor']),
            status: z.enum(ADSB_STATUS_VALUES),
            statusRaw: z.string().optional(),
            description: z.string().optional(),
            pageId: z.string(),
        }))
            .min(1)
            .optional(),
        // Per-row page provenance (QecListEventWithSource) — a single source page
        // per row, unlike the AD/SB `*PageIds` arrays, because consolidation keeps
        // each physical row tied to exactly one page.
        pageId: z.string(),
    })),
    // DR-4456: review flags on the published pack (see `statusPackWarningsSchema`).
    warnings: statusPackWarningsSchema,
});
//# sourceMappingURL=qec-list-events-ready.webhook.js.map